import Contrat, { ContratStatuts } from '@/generated/classes/Contrat';
import { EnvoiSignatureMasseQuery } from '@/extend/classes/ResultatContratMasse';
import { ControleSignatureMasseManqueCodes, ResultatContratMasseIssues } from '@/generated/classes/ResultatContratMasse';
import { type IssueControles } from '@/custom/helpers/signatureMasse';

// Confirmation de l'envoi en signature électronique en masse (OVA-5011), après les étapes de contrôle.
//
// La répartition couvre toute la sélection, chaque contrat dans une seule catégorie : le statut d'abord
// (déjà signé, déjà envoyé, non concerné), puis, pour les contrats encore envoyables, l'issue des étapes.
// Les catégories « déjà envoyés » et « non concernés » s'ajoutent aux trois lignes de l'US, pour que les
// nombres annoncés fassent le total de la sélection (proposition de l'arbitrage OVA-5057 §2).
//
// Le serveur refait tous ces contrôles à l'envoi (`envoyerSignatureMasse`) : la répartition n'est qu'une
// annonce, et un contrat peut encore être écarté au moment du traitement.
//
// L'envoi lui-même est confié au serveur, contrat par contrat ; le client découpe en tranches pour garder
// une progression et borner la durée de chaque requête, puis cumule les comptes rendus.

/** Statuts d'un contrat qui peut encore partir en signature : générer puis envoyer, ou envoyer. */
export const STATUTS_ENVOYABLES = [ContratStatuts.COMPUTED, ContratStatuts.GENERATED, ContratStatuts.MODIFIED];
/** Statuts d'un contrat déjà soumis au salarié, pas encore signé. */
export const STATUTS_DEJA_ENVOYES = [ContratStatuts.SUBMITTED, ContratStatuts.COMMENTED, ContratStatuts.APPROUVED];
/** Statuts d'un contrat déjà signé. */
export const STATUTS_DEJA_SIGNES = [ContratStatuts.SALARIE_SIGNED, ContratStatuts.SIGNED, ContratStatuts.CHECKED];

/**
 * Au-delà de ce nombre de contrats envoyables, la confirmation avertit de la durée : chaque envoi produit
 * un PDF puis appelle EduSign, soit quelques secondes par contrat (même seuil que la DPAE, proposition
 * de l'arbitrage OVA-5057 §5).
 */
export const SEUIL_AVERTISSEMENT_SIGNATURE = 50;

/**
 * Libellé du bouton qui lance l'envoi : « Confirmer », comme l'US (arbitrage OVA-5057 §4 du 14/10/2026).
 * SectionAlert répond ce même libellé à la touche Entrée, quels que soient les boutons affichés : la
 * confirmation neutralise donc Entrée tant qu'elle est ouverte (`neutraliserToucheEntree`), pour qu'un
 * envoi irréversible ne parte que sur un clic.
 */
export const BOUTON_ENVOI = 'Confirmer';

/**
 * Neutralise la touche Entrée jusqu'à l'appel de la fonction rendue. L'écouteur est posé sur `window` en
 * phase de capture : il passe avant celui de SectionAlert, posé sur `document` en phase de bouillonnement,
 * et arrête l'événement avant lui. Échap reste disponible pour annuler.
 */
export function neutraliserToucheEntree(): () => void {
  const intercepter = (event: KeyboardEvent): void => {
    if (event.key == 'Enter') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
  window.addEventListener('keydown', intercepter, true);
  return () => window.removeEventListener('keydown', intercepter, true);
}

export interface RepartitionEnvoi {
  /** Contrats à envoyer, dans l'ordre de la sélection. */
  envoyables: Contrat[];
  /** Contrats envoyables par leur statut, mais écartés par une étape (information manquante). */
  informationsManquantes: number;
  dejaSignes: number;
  dejaEnvoyes: number;
  /** Contrats non gérés, annulés, CI98, pas encore calculés, ou hors périmètre. */
  nonConcernes: number;
}

/** Répartit la sélection d'après les statuts et l'issue des étapes de contrôle. */
export function repartirEnvoi(contrats: Contrat[], issue: IssueControles): RepartitionEnvoi {
  const repartition: RepartitionEnvoi = {
    envoyables: [], informationsManquantes: 0, dejaSignes: 0, dejaEnvoyes: 0, nonConcernes: 0,
  };
  contrats.forEach((contrat) => {
    if (STATUTS_DEJA_SIGNES.includes(contrat.statut)) {
      repartition.dejaSignes++;
    } else if (STATUTS_DEJA_ENVOYES.includes(contrat.statut)) {
      repartition.dejaEnvoyes++;
    } else if (!STATUTS_ENVOYABLES.includes(contrat.statut)) {
      repartition.nonConcernes++;
    } else {
      const exclusion = issue.exclusions.get(contrat.id);
      if (exclusion == undefined) {
        repartition.envoyables.push(contrat);
      } else if (exclusion.manques.some((manque) => manque.code == ControleSignatureMasseManqueCodes.HORS_PERIMETRE)) {
        repartition.nonConcernes++;
      } else {
        repartition.informationsManquantes++;
      }
    }
  });
  return repartition;
}

/**
 * Taille des tranches envoyées au serveur. Petite, parce que chaque contrat coûte quelques secondes —
 * PDF puis EduSign — et qu'aucun délai d'attente n'est posé côté client : une tranche de 5 reste sous
 * la demi-minute, et la progression avance régulièrement.
 */
export const TAILLE_TRANCHE_SIGNATURE = 5;

/** Issues d'un contrat, rendues par le serveur (ContratEnvoiSignatureManager). */
export const ISSUE_ENVOYE = ResultatContratMasseIssues.ENVOYE;
export const ISSUE_IGNORE = ResultatContratMasseIssues.IGNORE;
export const ISSUE_A_COMPLETER = ResultatContratMasseIssues.A_COMPLETER;
export const ISSUE_ECHEC = ResultatContratMasseIssues.ECHEC;

/** Issue d'un contrat du lot, avec son motif (vide pour un contrat envoyé). */
export interface ResultatContrat {
  contratId: number;
  issue: ResultatContratMasseIssues;
  motif: string;
}

/**
 * Compte rendu d'un lot : l'issue de chaque contrat, et les compteurs qui en sont déduits (revue de code
 * du 15/10/2026). Invariant tenu par le serveur : envoyes + aCompleter + ignores + echecs == demandes.
 */
export interface ResultatEnvoiSignature {
  demandes: number;
  envoyes: number;
  /** Contrats non envoyés faute d'informations, relevées au moment du traitement. */
  aCompleter: number;
  /** Contrats qui n'étaient plus à envoyer au moment du traitement : déjà envoyés, signés… */
  ignores: number;
  /** Échecs techniques. */
  echecs: number;
  resultats: ResultatContrat[];
}

/**
 * Motif d'une tranche dont la réponse n'est pas parvenue (coupure réseau, erreur serveur) : le serveur a
 * pu traiter tout ou partie de ses contrats avant l'incident, l'utilisateur doit donc en vérifier l'état
 * plutôt que de les renvoyer à l'aveugle.
 */
export const MOTIF_TRANCHE_EN_ERREUR = 'Erreur technique, état du contrat à vérifier';

/** Compte rendu d'un lot vide, point de départ du cumul. */
export function resultatVide(): ResultatEnvoiSignature {
  return { demandes: 0, envoyes: 0, aCompleter: 0, ignores: 0, echecs: 0, resultats: [] };
}

/** Contrats du lot d'une issue donnée, avec leur motif. */
export function resultatsDe(resultat: ResultatEnvoiSignature, issue: ResultatContratMasseIssues): ResultatContrat[] {
  return resultat.resultats.filter((contrat) => contrat.issue == issue);
}

/**
 * Envoie en signature les contrats fournis, par tranches séquentielles (`envoyerSignatureMasse`).
 *
 * Séquentielles à dessein, comme la DPAE : chaque PDF passe par la file unique du serveur, et
 * paralléliser ne ferait qu'allonger chaque requête. Aucun échec n'interrompt le lot : une tranche
 * sans réponse est comptée en échec pour chacun de ses contrats, et le lot continue.
 *
 * `surProgression` est notifié après chaque tranche, pour alimenter le compteur d'avancement.
 */
export async function envoyerSignatureParLot(
  contrats: Contrat[],
  surProgression: (traites: number) => void,
  tailleTranche: number = TAILLE_TRANCHE_SIGNATURE,
): Promise<ResultatEnvoiSignature> {
  const resultat = resultatVide();
  const ids = contrats.map((contrat) => contrat.id);
  for (let debut = 0; debut < ids.length; debut += tailleTranche) {
    const tranche = ids.slice(debut, debut + tailleTranche);
    try {
      const query = new EnvoiSignatureMasseQuery();
      query.contratIds = tranche;
      const reponse = await Contrat.envoyerSignatureMasse(query);
      resultat.demandes += reponse.demandes;
      resultat.envoyes += reponse.envoyes;
      resultat.aCompleter += reponse.aCompleter;
      resultat.ignores += reponse.ignores;
      resultat.echecs += reponse.echecs;
      resultat.resultats.push(...(reponse.resultats ?? []));
    } catch {
      resultat.demandes += tranche.length;
      resultat.echecs += tranche.length;
      tranche.forEach((contratId) => {
        resultat.resultats.push({ contratId, issue: ISSUE_ECHEC, motif: MOTIF_TRANCHE_EN_ERREUR });
      });
    }
    surProgression(Math.min(debut + tailleTranche, ids.length));
  }
  return resultat;
}
